// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  usePropsValidation,
  type CompilerInjectedProps,
} from '../validation/runtime-checks/index.js';
import { types as t } from '../validation/runtime-checks/index.js';
import { ColumnInternal, RowContext, RowInternal } from '../grid.js';

import { Text, Bold, Italics } from '../Text.js';
import {
  AlgebraicWorking,
  AlgebraicWorkingStepIndicator,
} from '../AlgebraicWorking.js';
import BoxAndWhiskerPlot from '../BoxAndWhiskerPlot.js';
import Latex from '../Latex.js';
import { LatexTextAtom } from '../LatexTextAtom.js';
import { CartesianPlane } from '../CartesianPlane.js';
import DraftImage from '../DraftImage.js';
import Image from '../Image.js';
import { List } from '../List.js';
import { MultipleChoice } from '../MultipleChoice.js';
import { NumberLine } from '../NumberLine.js';
import { PseudoCodeBlock } from '../PseudoCodeBlock.js';
import { StemAndLeafPlot } from '../StemAndLeafPlot.js';
import TabularData from '../TabularData.js';
import { TwoColumn } from '../Columns.js';
import { TwoColumnProof } from '../TwoColumnProof.js';

const lochmara = '#0875BE';
const semiBoldProximaNova = 600;

// prettier-ignore
const romanNumerals = [
  "i", "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x",
  "xi", "xii", "xiii", "xiv", "xv", "xvi", "xvii", "xviii", "xix", "xx"
];

export function PerPartTasks(props: {|
  columns: 1 | 2 | 4,
  children: ChildrenArray<Element<typeof PerPartTask>>,
|}) {
  const rowContext = React.useContext(RowContext);
  const { failedValidation } = usePropsValidation(props, PerPartTasks);
  if (failedValidation) return null;
  /**
   * All columns have a 1 column gap between them as a "synthetic" gutter
   * in 2 and 4 columns there are 1 and 3 gutter columns each.
   *
   * 4 Column
   * +---+-+---+-+---+-+---+
   * |   | |   | |   | |   |
   * |   | |   | |   | |   |
   * |   | |   | |   | |   |
   * +---------------------+
   *
   * 2 Column
   * +---------------------+
   * |         | |         |
   * |         | |         |
   * |         | |         |
   * +---------+-+---------+
   */
  const { columns, children } = props;
  const availableColumns = {
    '1': rowContext.availableColumns,
    '2': rowContext.availableColumns - 1,
    '4': rowContext.availableColumns - 3,
  }[props.columns];
  if (availableColumns === 0) {
    throw new RangeError(
      'There are no available columns to render the Task elements in.',
    );
  }
  return (
    <div>
      <div style={{ height: 16, width: '100%' }} />
      {groupsOf(columns, React.Children.toArray(children)).map((row, i) => (
        <RowInternal key={i} style={{ breakInside: 'avoid' }}>
          {row.map((child, j) => (
            <React.Fragment key={j}>
              <ColumnInternal colSpan={Math.floor(availableColumns / columns)}>
                {React.cloneElement(child, { _index: i * columns + j })}
              </ColumnInternal>
              {j < row.length - 1 && <ColumnInternal colSpan={1} />}
            </React.Fragment>
          ))}
        </RowInternal>
      ))}
    </div>
  );
}
PerPartTasks.spec = {
  columns: t.unionOfValues(1, 2, 4),
  children: t.childrenArray(t.elementType(PerPartTask)),
};

// This function takes an array and splits it into groups of size n.
// For example, groupsOf(2, [1, 2, 3, 4]) returns [[1, 2], [3, 4]].
// Final group will be be smaller than n if array length is not a multiple of n
function groupsOf<T>(n: number, xs: Array<T>): Array<Array<T>> {
  const groups = [];
  for (let i = 0; i < xs.length; i += n) {
    groups.push(xs.slice(i, i + n));
  }
  return groups;
}

export function PerPartTask(props: {|
  label: string,
  _index?: number,
  children: ChildrenArray<
    | string
    | Element<typeof AlgebraicWorking>
    | Element<typeof AlgebraicWorkingStepIndicator>
    | Element<typeof Bold>
    | Element<typeof BoxAndWhiskerPlot>
    | Element<typeof CartesianPlane>
    | Element<typeof DraftImage>
    | Element<typeof Image>
    | Element<typeof Italics>
    | Element<typeof Latex>
    | Element<typeof LatexTextAtom>
    | Element<typeof List>
    | Element<typeof MultipleChoice>
    | Element<typeof NumberLine>
    | Element<typeof PseudoCodeBlock>
    | Element<typeof StemAndLeafPlot>
    | Element<typeof TabularData>
    | Element<typeof Text>
    | Element<typeof TwoColumn>
    | Element<typeof TwoColumnProof>,
  >,
  ...CompilerInjectedProps,
|}) {
  const { availableColumns } = React.useContext(RowContext);
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    PerPartTask,
  );

  // Ad-hoc validation to ensure the label is the correct roman numeral.
  // Ie, 0th Task should be "i", 1st Task should be "ii", etc.
  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;
    // If we aren't rendering Task as a child of PerPartTasks, we won't get
    // an _index prop. This is expected in certain situations (eg. rendering
    // an isolated Task extracted for a doc for data analysis, rendering in
    // the playground, etc.)
    if (props._index === undefined) return;
    const requiredLabel = romanNumerals[props._index];
    if (props.label !== requiredLabel) {
      addErrors([
        {
          message: `"${props.label}" is invalid, must be set to "${requiredLabel}"`,
          ...locations.label,
        },
      ]);
    }
  });

  if (failedValidation) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      <RowInternal>
        <ColumnInternal colSpan={1}>
          <span style={{ fontWeight: semiBoldProximaNova, color: lochmara }}>
            {props.label}
          </span>
        </ColumnInternal>
        <ColumnInternal colSpan={availableColumns - 1}>
          {props.children}
        </ColumnInternal>
      </RowInternal>
    </div>
  );
}
PerPartTask.spec = {
  label: taskLabelChecker,
  children: t.childrenArray(
    t.unionOfTypes(
      t.string,
      t.elementType(AlgebraicWorking),
      t.elementType(AlgebraicWorkingStepIndicator),
      t.elementType(Bold),
      t.elementType(BoxAndWhiskerPlot),
      t.elementType(CartesianPlane),
      t.elementType(DraftImage),
      t.elementType(Image),
      t.elementType(Italics),
      t.elementType(Latex),
      t.elementType(LatexTextAtom),
      t.elementType(List),
      t.elementType(MultipleChoice),
      t.elementType(NumberLine),
      t.elementType(PseudoCodeBlock),
      t.elementType(StemAndLeafPlot),
      t.elementType(TabularData),
      t.elementType(Text),
      t.elementType(TwoColumn),
      t.elementType(TwoColumnProof),
    ),
  ),
};

function taskLabelChecker(props, propName) {
  const label = props[propName];

  // Ensure it is of type string
  const failure = t.string(props, propName);
  if (failure) return failure;

  // Type narrowing, t.string() check above should ensure this.
  // branch is never taken in practice.
  if (typeof label !== 'string') return null;

  // Ensure the label is a roman numeral
  if (romanNumerals.includes(label) === false) {
    return {
      typeDescription: taskLabelChecker.description,
      reason: `must be a lowercase roman numeral`,
    };
  }

  return null;
}
taskLabelChecker.description = `Valid label string`;
